import { Group, Object3D, Vector3, VectorKeyframeTrack, type KeyframeTrack } from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { fetchWithProgress } from '../util/fetchWithProgress';

export type ProgressCallback = (loaded: number, total: number) => void;

export interface LoadedMachine {
  /** The original loaded scene; contains all meshes from the GLB. */
  root: Group;
  /** The raw GLTF result if a caller needs animation clips, etc. */
  gltf: GLTF;
  /**
   * If the GLB contains a baked reveal animation, the in-motion segment of that animation as
   * world-space positions (relative to the scene root). The model creator designed this path —
   * it represents the ball travelling from chamber → through the machine's tubes → to the
   * result dock. We re-use it as the reveal-animation curve.
   *
   * Empty when no such animation exists; the caller falls back to a synthesized arch.
   */
  exitPath: Vector3[];
}

/** Node names in the GLB whose .position track represents the model creator's "winning ball". */
const WIN_NODE_CANDIDATES = ['Ball1', 'WinBall', 'ResultBall'];

export async function loadMachine(
  url: string,
  onProgress: ProgressCallback = () => {},
): Promise<LoadedMachine> {
  // Stream the GLB ourselves so the host gets byte-accurate progress. Then feed the buffer
  // to GLTFLoader.parse() — which doesn't accept onProgress but works fine on an in-memory
  // ArrayBuffer. The GLB is meshopt-compressed (EXT_meshopt_compression); register the
  // WASM decoder before parsing.
  const buffer = await fetchWithProgress(url, onProgress);
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const gltf = await new Promise<GLTF>((resolve, reject) => {
    loader.parse(buffer, '', resolve, reject);
  });
  const root = gltf.scene;
  // Ensure all transforms are baked into world matrices before mapping.
  root.updateMatrixWorld(true);
  const exitPath = extractTrackPath(gltf, WIN_NODE_CANDIDATES);
  return { root: root as Group, gltf, exitPath };
}

/**
 * Deep-clone an Object3D including geometry refs (default) and shared materials.
 * For the multi-machine Sicbo layout — we load the GLB once and clone the root for the other two.
 */
export function cloneMachine(src: Object3D): Object3D {
  return src.clone(true);
}

/**
 * Walk the GLTF's animations looking for the first matching named node. Find that node's
 * .position track, identify the time window where it's actually moving (skipping
 * the stationary lead-in and tail-off), and sample ~30 evenly-spaced points along that window.
 */
function extractTrackPath(gltf: GLTF, nodeCandidates: readonly string[]): Vector3[] {
  const clip = gltf.animations?.[0];
  if (!clip) return [];

  let track: VectorKeyframeTrack | null = null;
  for (const name of nodeCandidates) {
    const t = clip.tracks.find((tr: KeyframeTrack) => tr.name === `${name}.position`);
    if (t && t instanceof VectorKeyframeTrack) {
      track = t;
      break;
    }
  }
  if (!track) return [];

  const times = track.times;
  const values = track.values;
  const keyCount = times.length;
  if (keyCount < 2) return [];

  // Build keyframe array of Vector3s.
  const keys: Vector3[] = [];
  for (let i = 0; i < keyCount; i++) {
    keys.push(new Vector3(values[i * 3]!, values[i * 3 + 1]!, values[i * 3 + 2]!));
  }

  // Find the in-motion window.
  const lead = keys[0]!;
  const tail = keys[keyCount - 1]!;
  const threshold = 0.05;
  let firstMoving = 0;
  for (let i = 0; i < keyCount; i++) {
    if (keys[i]!.distanceTo(lead) > threshold) {
      firstMoving = Math.max(0, i - 1);
      break;
    }
  }
  let lastMoving = keyCount - 1;
  for (let i = keyCount - 1; i >= 0; i--) {
    if (keys[i]!.distanceTo(tail) > threshold) {
      lastMoving = Math.min(keyCount - 1, i + 1);
      break;
    }
  }
  if (lastMoving <= firstMoving + 1) return [];

  // Sample ~30 evenly-spaced keyframes across [firstMoving, lastMoving].
  const targetSamples = 30;
  const segment = keys.slice(firstMoving, lastMoving + 1);
  if (segment.length <= targetSamples) return segment.map((v) => v.clone());
  const step = (segment.length - 1) / (targetSamples - 1);
  const out: Vector3[] = [];
  for (let i = 0; i < targetSamples; i++) {
    out.push(segment[Math.round(i * step)]!.clone());
  }
  return out;
}
